import { useEffect, useState } from 'react';
import { call, getToken, venue as api } from './api.js';
import { withReturn, hereNow } from './gate.jsx';

/* ══════════════════════════════════════════════════════════════════════════
 * RESERVE — `Eleater Prototype/reservation.html`
 *
 * ⚠️ UNTIL NOW, TAPPING RESERVE OPENED THE RESTAURANT'S BOOKING PAGE AND ELEAT
 * LEARNED NOTHING ELSE. No form before, no question after. Eleat knew somebody
 * left and knew nothing about what happened.
 *
 * ── THE SIX STEPS, AND WHY STEP 1 IS THE COMMERCIAL ONE ─────────────────────
 *
 *   1 · ELEAT'S OWN FORM — day, time, party, occasion, allergies, requests
 *   2 · hand off to the restaurant's provider, recording the click-out
 *   3 · they fill it in again, over there
 *   4 · back on the webview: "Has the restaurant replied yet?"
 *   5 · Yes → thanked, and told plainly what "yes" means
 *   6 · No  → send it again, or go and find another place
 *
 * ⚠️ STEP 1 IS EVERYTHING ELEAT WILL EVER KNOW ABOUT THIS BOOKING. The
 * Reservation Routing Disclosure, clause 4: *"In the current webview model,
 * Eleat is blind inside the provider page."* Once they are over there, nothing
 * comes back. Skip this screen and Eleat has a link-out and no data — which is
 * what it had this morning.
 *
 * ⚠️ AND IT IS NOT A PRE-FILL. reservations.routes.js says so, from a real
 * trial: *"The partner page ignores URL params… so this is OUR record, not a
 * pre-fill."* The Eleater types it twice. That is the honest cost of not being
 * the booking system, and it buys Eleat the thing it actually sells: knowing
 * that its own demand turned into a table.
 *
 * ── ⚠️ ELEAT DOES NOT MAKE THE BOOKING, AND NEVER SAYS IT DID ───────────────
 *
 * Clause 1: the provider or restaurant controls inventory, confirmation,
 * cancellation and fulfilment. Clause 3: *"'Yes, I made a reservation' means
 * only that the Eleater told Eleat they completed a reservation. It is not
 * confirmation."* The glossary keeps three separate words — reservation_click
 * (intent), self_reported_booking (they said so), confirmed_reservation (Eleat
 * holds it and the restaurant accepted). Nothing on this screen may blur them.
 *
 * ⚠️ AND NO PROVIDER IS NAMED IN THIS FILE. Al, 21 September 2026: *"it could
 * be Eats365. It could be OpenTable. It could be various other ones. So it's
 * not just the one."* The provider is a field on the venue. A provider name
 * written into a screen turns a field into a feature.
 *
 * Every word an Eleater reads below is quoted from `reservation.html`.
 * ══════════════════════════════════════════════════════════════════════════ */

const PARTY = [1, 2, 3, 4, 5, 6, 7, 8];

/* ⚠️ FOURTEEN DAYS, NAMED, NOT A DATE PICKER. The locked screen asks "Which
   day" as a strip — and a native date input on a phone opens a calendar that
   takes four taps to answer "tomorrow", which is what most people want. */
/* ⚠️ THE VENUE'S OWN CALENDAR AND CLOCK — 7 Oct 2026. The days came off the
   phone's date read in UTC (toISOString), so before 8 a.m. in Hong Kong
   "Today" was yesterday; and at 10:28 PM "Today" still offered 12:00. The
   days and the times left today are the restaurant's, in its time zone —
   the same wall clock `reserved_for` is read in. */
function venueNow(tz) {
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: tz || undefined, year: 'numeric', month: '2-digit',
    day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const p = Object.fromEntries(f.formatToParts(new Date()).map((x) => [x.type, x.value]));
  return { iso: `${p.year}-${p.month}-${p.day}`, hm: `${p.hour}:${p.minute}` };
}
function nextDays(n, tz) {
  const out = [];
  const [y, m, d0] = venueNow(tz).iso.split('-').map(Number);
  for (let i = 0; i < n; i++) {
    const d = new Date(Date.UTC(y, m - 1, d0 + i, 12));
    out.push({
      iso: d.toISOString().slice(0, 10),
      label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow'
        : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }),
    });
  }
  return out;
}

/* an ISO date n days after another (a calendar step, no time zone) */
function laterIso(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n, 12)).toISOString().slice(0, 10);
}

const TIMES = ['12:00', '12:30', '13:00', '13:30', '18:00', '18:30',
  '19:00', '19:30', '20:00', '20:30', '21:00', '21:30'];

export default function Reserve({ id }) {
  const signedIn = !!getToken();
  const [v, setV] = useState(null);
  const [err, setErr] = useState(null);

  /* which of the three faces this screen is wearing */
  /* `&details=<booking>` — "Did you make a reservation?" answered Yes on the
     next open (bookask.jsx) lands straight on "When for, and how many?" */
  const detailsFor = new URLSearchParams(window.location.search).get('details');
  const [step, setStep] = useState(detailsFor ? 'details' : 'form');   // form → sent → done

  const tz = v && v.timezone;
  const clock = venueNow(tz);
  /* today keeps only the times still ahead; a late evening has none, and
     then "Today" is not offered at all */
  const timesFor = (iso) => (iso === clock.iso ? TIMES.filter((t) => t > clock.hm) : TIMES);
  const days = nextDays(14, tz).filter((d) => timesFor(d.iso).length);
  const [day, setDay] = useState(days[0].iso);
  const [time, setTime] = useState('19:00');
  const times = timesFor(day);
  /* the venue's answer arrives after the first draw: keep the choice valid */
  useEffect(() => {
    /* a date picked weeks ahead is kept; only a past day, or a today with
       no times left, goes back to the first offered day */
    if (day < days[0].iso || !timesFor(day).length) setDay(days[0].iso);
    else if (!times.includes(time) && times.length) setTime(times.includes('19:00') ? '19:00' : times[0]);
  }, [day, time, days.map((d) => d.iso).join(), times.join()]);
  const [party, setParty] = useState(2);
  const [occasion, setOccasion] = useState('');
  const [allergies, setAllergies] = useState('');
  const [requests, setRequests] = useState('');

  const [busy, setBusy] = useState(false);
  const [resId, setResId] = useState(detailsFor || null);
  const [answer, setAnswer] = useState(detailsFor ? 'yes' : null);   // 'yes' | 'no'
  /* 148 — the restaurant's booking page, open inside Eleat */
  const [sheet, setSheet] = useState(false);
  const [sheetReady, setSheetReady] = useState(false);
  /* 190 — a CONNECTED reservation app: Eleat's reference rides on the link
     (in the field the app echoes back, reservation_ref_param) so the app's
     booking finds this click-out. Unconnected: the link exactly as saved. */
  const [bookUrl, setBookUrl] = useState(null);
  /* BOOK WITHOUT LEAVING ELEAT (Al-Noor, 12 Oct 2026) — a restaurant connected
     to an app that lets Eleat book: its real free times, booked here, no web
     view. A time that needs a card, or the app not answering, falls back to
     the restaurant's own page. provider.booking.js on the server. */
  const [appTimes, setAppTimes] = useState(null);      // null · 'loading' · 'failed' · [{time, needs_card}]
  const [pick, setPick] = useState(null);
  const [booked, setBooked] = useState(null);
  useEffect(() => {
    if (!sheet) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setSheet(false); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [sheet]);

  useEffect(() => {
    let live = true;
    api.get(id)
      .then((d) => { if (live) setV(d); })
      .catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [id]);

  const inEleat = !!(v && v.reservation_in_eleat) && appTimes !== 'failed';
  /* ⚠️ NO DOUBLE ENTRY — Al-Noor, 21 Oct 2026: "Let's not have double entry
     for now… park that double entry until we get APIs." A restaurant with a
     booking page skips Eleat's own form (step 1 of ELEAT_RESERVATION_FLOW.md,
     parked, not deleted): one line saying where they are going, the page
     opens (inside Eleat where it can), and "Has the restaurant replied yet?"
     on the way back, as before. A connected app (inEleat) keeps its own
     screen. */
  const handoff = !inEleat && !!(v && v.reservation_url);
  useEffect(() => {
    if (!v || !v.reservation_in_eleat) return undefined;
    let live = true;
    setAppTimes((t) => (t === 'failed' ? t : 'loading')); setPick(null);
    call(`/reservations/restaurant/${encodeURIComponent(id)}/app-times?date=${day}&party=${party}`)
      .then((d) => { if (live) setAppTimes(Array.isArray(d && d.times) ? d.times : 'failed'); })
      .catch(() => { if (live) setAppTimes('failed'); });
    return () => { live = false; };
  }, [v && v.reservation_in_eleat, id, day, party]);
  const slot = Array.isArray(appTimes) ? appTimes.find((t) => t.time === pick) : null;

  async function bookHere(e) {
    e.preventDefault();
    if (busy || !slot) return;
    if (slot.needs_card) return sendRequest(e, slot.time);   // a card is needed: the restaurant's own page
    setBusy(true); setErr(null);
    try {
      const d = await call(`/reservations/restaurant/${encodeURIComponent(id)}/app-book`, {
        method: 'POST', auth: true,
        body: { date: day, time: slot.time, party_size: Number(party), share_details: true,
          occasion: occasion.trim() || undefined, special_requests: requests.trim() || undefined,
          dietary_notes: allergies.trim() || undefined },
      });
      setBooked(d); setStep('booked');
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  }

  async function sendRequest(e, atTime) {
    e.preventDefault();
    if (busy || !v) return;
    /* ⚠️⚠️ THE PROVIDER'S PAGE OPENS ON THE TAP ITSELF — 1 October 2026.
       It used to open AFTER Eleat had recorded the request, and Safari only
       lets a page open a new tab during the tap that asked for it: after a
       network wait (1.5s+ on Al's connection) it is quietly blocked, so the
       booking page never appeared and nothing said so. Opened first, here,
       while the tap is still the reason; the record is written alongside it. */
    let ref = null; let url = v.reservation_url;
    if (url && v.reservation_ref_param) {
      ref = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID()
        : 'e' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
      try { const u = new URL(url); u.searchParams.set(v.reservation_ref_param, ref); url = u.toString(); }
      catch { ref = null; }
    }
    setBookUrl(url);
    const opened = !!url;
    /* ⚠️ INSIDE ELEAT, AS AL DESIGNED IT — 6 Oct 2026. ELEAT_RESERVATION_FLOW.md:
       "they're taken to the reservation app of the restaurant… then it comes
       back on the web view, says did you reserve?" The page opens in a sheet
       over this screen; closing it leaves them on "Has the restaurant replied
       yet?". A link its provider refuses to show inside another site
       (reservation_providers.frame_blocked_pattern, 148) opens a new tab, as
       before — on the tap, for the same Safari reason. */
    if (opened && v.reservation_opens_inside) { setSheetReady(false); setSheet(true); }
    else if (opened) window.open(url, '_blank', 'noopener');
    setBusy(true); setErr(null);
    try {
      /* ⚠️ RECORDED BEFORE THEY LEAVE, because after they leave Eleat is
         blind. `reserved_for` is the venue's own wall clock — the same rule
         as every other time in Eleat. */
      const d = await call(`/reservations/restaurant/${encodeURIComponent(id)}/clickout`, {
        method: 'POST', auth: true,
        body: {
          provider: v.reservation_provider || undefined,
          source_screen: 'reservation',
          /* the offer that was on screen when Reserve was tapped */
          deal_id: /^[0-9a-f-]{36}$/i.test(new URLSearchParams(window.location.search).get('deal') || '')
            ? new URLSearchParams(window.location.search).get('deal') : undefined,
          /* the form is parked (Al-Noor, 21 Oct 2026) — no details are asked,
             so none are sent; the click-out itself is still recorded */
          party_size: handoff ? undefined : Number(party),
          reserved_for: handoff ? undefined : `${day}T${atTime || time}`,
          occasion: handoff ? undefined : (occasion.trim() || undefined),
          special_requests: handoff ? undefined : (requests.trim() || undefined),
          dietary_notes: handoff ? undefined : (allergies.trim() || undefined),
          eleat_ref: ref || undefined,
        },
      });
      setResId(d?.id || d?.reservation_id || null);

      setStep('sent');
    } catch (e2) {
      /* the provider's page is already open and they are booking there —
         Eleat failing to file its own record is Eleat's problem, not a
         reason to hold them on an error */
      if (opened) setStep('sent');
      else setErr(e2.message);
    } finally { setBusy(false); }
  }

  async function record(outcome, recovery) {
    setAnswer(outcome);
    /* a "yes" after the restaurant's own page → "when for, and how many?"
       (Al-Noor, 21 Oct 2026). ⚠️ REQUIRED since 3 Nov 2026 (Al-Noor: "Ask for
       Date and Time"): the date is when Eleat asks "Did you make it?" — the
       day after it, however many weeks away the booking is (review.ask.js) */
    setStep(outcome === 'yes' && handoff && resId ? 'details' : 'done');
    /* ⚠️ NEVER AWAITED IN FRONT OF THE ELEATER. The answer is theirs; whether
       Eleat managed to file it is Eleat's problem. And an outcome with no
       reservation id has nowhere to go — that happens when the click-out
       itself failed, and it is not worth a second error on their screen. */
    if (!resId) return;
    call(`/reservations/${encodeURIComponent(resId)}/outcome`, {
      method: 'POST', auth: true,
      body: { outcome, ...(recovery ? { recovery } : {}) },
    }).catch(() => { /* their evening matters more than our funnel */ });
  }

  if (!signedIn) {
    return (
      <div className="screen resv">
        <div className="top"><a className="back" href={`?venue=${id}`} aria-label="Back">‹</a></div>
        <main>
          <>
          <p className="mut">
            Sign in to reserve.
          </p>
          <div className="guest">
            <a className="gcta" href={withReturn('?welcome=1', hereNow())}>Sign in or join Eleat</a>
          </div>
          </>
        </main>
      </div>
    );
  }

  if (!v) {
    return (
      <div className="screen resv">
        <main><p className="mut">{err || 'Loading…'}</p></main>
      </div>
    );
  }

  /* the booking sheet — the restaurant's own page, with a way out (✕) and a
     way to open it in a new tab if it will not load here */
  const sheetEl = sheet && v && v.reservation_url ? (
    <div className="bksheet" role="dialog" aria-modal="true" aria-label={v.name}>
      <div className="bkbar">
        <b>{v.name}</b>
        <a className="bkout" href={bookUrl || v.reservation_url} target="_blank" rel="noopener noreferrer"
          aria-label="Open in a new tab" title="Open in a new tab">&#8599;</a>
        <button type="button" className="bkx" aria-label="Close" onClick={() => setSheet(false)}>&#10005;</button>
      </div>
      {/* a booking page can take several seconds — never a blank panel */}
      {!sheetReady && <p className="bkwait mut">Loading…</p>}
      <iframe className="bkframe" src={bookUrl || v.reservation_url} title={v.name} allow="payment"
        onLoad={() => setSheetReady(true)} />
    </div>
  ) : null;

  /* "See other restaurants nearby" (Al-Noor, 12 Oct 2026 — replaces "Find
     another place"): Explore with the same kind of food, near them */
  const othersHref = '?explore=1' + (v.primary_cuisine_label ? '&q=' + encodeURIComponent(v.primary_cuisine_label) : '') + '&not=' + encodeURIComponent(id);

  /* ── BOOKED HERE — the app made the booking; it is confirmed ──────────── */
  if (step === 'booked' && booked) {
    const dayLabel = (days.find((d) => d.iso === day) || {}).label || day;
    return (
      <div className="screen resv">
        <div className="top"><a className="back" href={`?venue=${id}`} aria-label="Back">‹</a></div>
        <main>
          <h1>You’re booked</h1>
          <p className="sub">{[v.name, dayLabel, pick, `${party} people`].join(' · ')}</p>
          <p className="mut">{`Confirmed by ${booked.app || v.reservation_app}.`}</p>
          <a className="forgot" href="?reservations=1">See it in Reservations</a>
          <a className="forgot" href={`?venue=${id}`}>Back to Eleat</a>
        </main>
      </div>
    );
  }

  /* ── CONNECTED — the restaurant's app tells Eleat what happens, so the
     Eleater is not asked "Has the restaurant replied yet?" (Al-Noor, 12 Oct) ── */
  if (step === 'sent' && v.reservation_connected) {
    return (
      <div className="screen resv">
        {sheetEl}
        <div className="top"><a className="back" href={`?venue=${id}`} aria-label="Back">‹</a></div>
        <main>
          <h1>Your request is with the restaurant</h1>
          <p className="sub">{`It shows in Reservations as soon as ${v.name} confirms it.`}</p>
          <a className="forgot" href="?reservations=1">See it in Reservations</a>
          {/* no table there? help them straight on to the next one */}
          <a className="forgot" href={othersHref}>See other restaurants nearby</a>
          <a className="forgot" href={`?venue=${id}`}>Back to Eleat</a>
        </main>
      </div>
    );
  }

  /* ── STEP 3 · the answer is in ────────────────────────────────────────── */
  if (step === 'done') {
    return (
      <div className="screen resv">
        <div className="top"><a className="back" href={`?venue=${id}`} aria-label="Back">‹</a></div>
        <main>
          {answer === 'yes' ? (
            <>
              <h1>Thank you — noted</h1>
              {/* ⚠️ SAID EXACTLY, BECAUSE "YES" IS NOT A CONFIRMATION.
                  It is the Eleater telling Eleat what they did. */}
              <p className="sub">Your request is with the restaurant. They will confirm by message.</p>
              <p className="mut">Requested through {v.reservation_provider || 'the restaurant'}.</p>
              <a className="forgot" href="?reservations=1">See it in Reservations</a>
            </>
          ) : (
            <>
              <h1>No problem</h1>
              <p className="sub">
                No reservation yet. You can send the request again, or see other
                restaurants nearby.
              </p>
              {/* ⚠️ BOTH WAYS FORWARD, AND NEITHER IS A DEAD END — Al's own
                  "would you like to make another booking, or do you wanna find
                  another place". Choosing one is recorded as the recovery. */}
              <button className="cta" type="button"
                onClick={() => { setStep('form'); setAnswer(null); }}>
                Send the request again
              </button>
              <a className="forgot" href={othersHref}
                onClick={() => record('no', 'viewed_alternatives')}>
                See other restaurants nearby
              </a>
            </>
          )}
          <a className="forgot" href={`?venue=${id}`}>Back to Eleat</a>
        </main>
      </div>
    );
  }

  /* ── STEP 2½ · when for, and how many — required (3 Nov 2026) ───────────── */
  if (step === 'details') {
    const saveDetails = () => {
      setStep('done');
      if (!resId) return;
      call(`/reservations/${encodeURIComponent(resId)}/details`, {
        method: 'POST', auth: true, body: { reserved_for: `${day}T${time}`, party_size: Number(party) },
      }).catch(() => { /* theirs to tell; ours to file */ });
    };
    return (
      <div className="screen resv">
        <div className="top"><a className="back" href={`?venue=${id}`} aria-label="Back">‹</a></div>
        <main>
          {/* OpenTable's own words (3 Nov 2026): Reservation details · Date · Time · Party size */}
          <h1>Reservation details</h1>
          <div className="label">Date</div>
          <div className="chipbox">
            {days.map((d) => (
              <button key={d.iso} type="button" className={day === d.iso ? 'chip on' : 'chip'}
                aria-pressed={day === d.iso} onClick={() => setDay(d.iso)}>{d.label}</button>
            ))}
          </div>
          {/* a booking weeks ahead — any date in the next four months */}
          <input className="tin" type="date" value={days.some((d) => d.iso === day) ? '' : day}
            min={clock.iso} max={laterIso(clock.iso, 120)}
            onChange={(e) => { if (e.target.value) setDay(e.target.value); }} />
          <div className="label">Time</div>
          <div className="chipbox">
            {times.map((t) => (
              <button key={t} type="button" className={time === t ? 'chip on' : 'chip'}
                aria-pressed={time === t} onClick={() => setTime(t)}>{t}</button>
            ))}
          </div>
          <div className="label">Party size</div>
          <div className="chipbox">
            {PARTY.map((n) => (
              <button key={n} type="button" className={party === n ? 'chip on' : 'chip'}
                aria-pressed={party === n} onClick={() => setParty(n)}>{n}</button>
            ))}
          </div>
          <button className="cta" type="button" onClick={saveDetails}>Save</button>
        </main>
      </div>
    );
  }

  /* ── STEP 2 · they have come back ─────────────────────────────────────── */
  if (step === 'sent') {
    return (
      <div className="screen resv">
        {sheetEl}
        <div className="top"><a className="back" href={`?venue=${id}`} aria-label="Back">‹</a></div>
        <main>
          {/* Al-Noor's words, 3 Nov 2026: "Did you make a Reservation", Yes and No */}
          <h1>{`Did you make a reservation at ${v.name}?`}</h1>
          <button className="cta" type="button" onClick={() => record('yes')}>Yes</button>
          <button className="forgot" type="button" onClick={() => record('no')}>No</button>
        </main>
      </div>
    );
  }

  /* ── STEP 1 · the hand-off (the form is parked) ───────────────────────── */
  if (handoff) {
    const app = v.reservation_app || v.reservation_provider || null;
    return (
      <form className="screen resv" onSubmit={sendRequest} noValidate>
        {sheetEl}
        <div className="top"><a className="back" href={`?venue=${id}`} aria-label="Back">‹</a></div>
        <main>
          <h1>Reserve at {v.name}</h1>
          {/* new copy, Al-Noor's own words, 21 Oct 2026 */}
          <p className="sub">{app
            ? `You're now being taken to ${app}, the restaurant's preferred reservation system.`
            : "You're now being taken to the restaurant's preferred reservation system."}</p>
          {err ? <p className="err" aria-live="polite">{err}</p> : null}
          <button className="cta" type="submit" disabled={busy}>Reserve a Table</button>
        </main>
      </form>
    );
  }

  /* ── STEP 1 · Eleat's own form ────────────────────────────────────────── */
  return (
    <form className="screen resv" onSubmit={inEleat ? bookHere : sendRequest} noValidate>
      {sheetEl}
      <div className="top">
        <a className="back" href={`?venue=${id}`} aria-label="Back">‹</a>
      </div>

      <main>
        <h1>Reserve at {v.name}</h1>
        <p className="sub">{inEleat ? 'Choose a time and book it here' : 'Eleat sends this request to the restaurant'}</p>

        <div className="label">Which day</div>
        <div className="chipbox">
          {days.map((d) => (
            <button key={d.iso} type="button"
              className={day === d.iso ? 'chip on' : 'chip'}
              aria-pressed={day === d.iso}
              onClick={() => setDay(d.iso)}>{d.label}</button>
          ))}
        </div>

        {inEleat ? (
          <>
            <div className="label">Choose a time</div>
            {appTimes === 'loading' || appTimes === null ? <p className="mut">Loading…</p>
              : appTimes.length === 0 ? <p className="mut">No free tables that day. Try another day.</p>
              : (
                <div className="chipbox">
                  {appTimes.map((t) => (
                    <button key={t.time} type="button"
                      className={pick === t.time ? 'chip on' : 'chip'}
                      aria-pressed={pick === t.time}
                      onClick={() => setPick(t.time)}>{t.time}</button>
                  ))}
                </div>
              )}
            {slot && slot.needs_card && (
              <p className="mut">This time needs a card to hold it, so you book it on the restaurant’s own page.</p>
            )}
          </>
        ) : (
          <>
            <div className="label">Around what time</div>
            <div className="chipbox">
              {times.map((t) => (
                <button key={t} type="button"
                  className={time === t ? 'chip on' : 'chip'}
                  aria-pressed={time === t}
                  onClick={() => setTime(t)}>{t}</button>
              ))}
            </div>
          </>
        )}

        <div className="label">Party</div>
        <div className="chipbox">
          {PARTY.map((n) => (
            <button key={n} type="button"
              className={party === n ? 'chip on' : 'chip'}
              aria-pressed={party === n}
              onClick={() => setParty(n)}>{n}</button>
          ))}
        </div>

        <div className="label">Occasion <span className="mut">(optional)</span></div>
        <input className="tin" type="text" value={occasion}
          aria-label="Occasion"
          onChange={(e) => setOccasion(e.target.value)} />

        {/* ⚠️ ALLERGIES ARE SAFETY INFORMATION, NOT A PREFERENCE, and they stay
            with this booking. Routing Disclosure clause 5: dietary notes are
            "reservation-specific safety information and are not copied to the
            profile under the current schema rule." */}
        <div className="label">Allergies <span className="mut">(tell the kitchen)</span></div>
        <input className="tin" type="text" value={allergies}
          placeholder="Nuts, shellfish, gluten…" aria-label="Allergies"
          onChange={(e) => setAllergies(e.target.value)} />

        <div className="label">
          Special requests <span className="mut">(optional — seating, accessibility, dietary)</span>
        </div>
        <input className="tin" type="text" value={requests}
          placeholder="Window seat if possible" aria-label="Special requests"
          onChange={(e) => setRequests(e.target.value)} />

        {err && <div className="err" aria-live="polite">{err}</div>}
      </main>

      <footer>
        {inEleat && !(slot && slot.needs_card) ? (
          <>
            <button className="cta" type="submit" disabled={busy || !slot}>Book this table</button>
            {/* the Eleater's details leave Eleat here — said on the button's own line */}
            <p className="mut">{`Eleat sends your name, email and mobile to ${v.reservation_app} to make this booking.`}</p>
          </>
        ) : (
          <>
            <button className="cta" type="submit" disabled={busy}>Send my request</button>
            {/* ⚠️ THE LOCKED SCREEN'S OWN SENTENCE, and it is accurate: what Eleat
                sends on is what was entered HERE. */}
            <p className="mut">Eleat sends this to the restaurant with everything you entered here.</p>
          </>
        )}
      </footer>
    </form>
  );
}
